import { useState, useEffect, useCallback } from 'react';
import { imagesAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';

export const ImageGallery = ({ images = [], fallbackIcon, alt = 'Gallery' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const resolveUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return imagesAPI.getUrl(path);
  };

  const goTo = useCallback((idx) => {
    setActiveIndex((idx + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
      if (e.key === 'ArrowRight') goTo(activeIndex + 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, activeIndex, goTo]);

  if (!images || images.length === 0) {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center rounded-xl">
        {fallbackIcon || <span className="text-muted-foreground">No images</span>}
      </div>
    );
  }

  const activeUrl = resolveUrl(images[activeIndex]);

  return (
    <>
      {/* Hero Image */}
      <div className="relative group">
        <div
          className="relative aspect-[16/9] max-h-[500px] w-full overflow-hidden rounded-xl bg-muted cursor-pointer"
          onClick={() => setLightboxOpen(true)}
          data-testid="gallery-hero"
        >
          <img
            src={activeUrl}
            alt={`${alt} ${activeIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-black/50 text-white p-2 rounded-lg backdrop-blur-sm">
              <ZoomIn className="h-5 w-5" />
            </div>
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex - 1); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex + 1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                data-testid="gallery-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {/* Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-sm">
              {activeIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-thin" data-testid="gallery-thumbnails">
            {images.map((path, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={cn(
                  'flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all',
                  idx === activeIndex
                    ? 'border-primary ring-1 ring-primary/30 scale-105'
                    : 'border-transparent opacity-70 hover:opacity-100'
                )}
                data-testid={`gallery-thumb-${idx}`}
              >
                <img
                  src={resolveUrl(path)}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2"
            data-testid="lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="relative max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={activeUrl}
              alt={`${alt} ${activeIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-sm"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-sm"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Lightbox thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[80vw] pb-1">
            {images.map((path, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setActiveIndex(idx); }}
                className={cn(
                  'flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all',
                  idx === activeIndex
                    ? 'border-white scale-110'
                    : 'border-transparent opacity-50 hover:opacity-80'
                )}
              >
                <img
                  src={resolveUrl(path)}
                  alt={`Thumb ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
